import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";

import { toggleFavorite } from "./textsSlice";
import { sharedApi, getAuthToken } from "../../../services/sharedApi";
import { toApiError } from "../../../utils/errorUtils";

import type { ReceivedMaterial, TextListItem } from "@lingoshade/api";
import type { MaterialInvitation, StudentAssignment } from "@lingoshade/domain";
import type { PayloadAction } from "@reduxjs/toolkit";

interface MaterialsState {
  studentAssignments: {
    id: string | null;
    requestId: string | null;
    loading: boolean;
    busy: boolean;
    error: string | null;
    assignments: StudentAssignment[];
    catalog: TextListItem[];
    hasMore: boolean;
  };
  receivedRequestId: string | null;
  invitationsTextId: string | null;
  received: ReceivedMaterial[];
  total: number;
  loading: boolean;
  busy: boolean;
  error: string | null;
  invitations: MaterialInvitation[];
  filters: {
    language?: string;
    level?: string;
    hidden: boolean;
    search?: string;
  };
}
const initialState: MaterialsState = {
  studentAssignments: {
    id: null,
    requestId: null,
    loading: false,
    busy: false,
    error: null,
    assignments: [],
    catalog: [],
    hasMore: false,
  },
  receivedRequestId: null,
  invitationsTextId: null,
  received: [],
  total: 0,
  loading: false,
  busy: false,
  error: null,
  invitations: [],
  filters: { hidden: false },
};
function errorCode(error: unknown): string {
  const parsed = toApiError(error);
  return parsed.code || parsed.message || "MATERIAL_TECHNICAL_ERROR";
}
interface AssignmentFilters {
  language?: string;
  level?: string;
  search?: string;
}
export const fetchStudentAssignments = createAsyncThunk<
  {
    assignments: StudentAssignment[];
    catalog: TextListItem[];
    hasMore: boolean;
  },
  { id: string; filters?: AssignmentFilters },
  { rejectValue: string }
>(
  "studentAssignments/load",
  async (
    { id, filters },
    { rejectWithValue },
  ): Promise<
    | {
        assignments: StudentAssignment[];
        catalog: TextListItem[];
        hasMore: boolean;
      }
    | ReturnType<typeof rejectWithValue>
  > => {
    try {
      const [response, library] = await Promise.all([
        sharedApi.students.assignments(id, getAuthToken()),
        sharedApi.texts.list(
          { filter: "mine", limit: 50, ...filters },
          getAuthToken(),
        ),
      ]);
      return {
        assignments: response.assignments,
        catalog: library.texts,
        hasMore: library.pagination.hasMore,
      };
    } catch (error: unknown) {
      return rejectWithValue(errorCode(error));
    }
  },
);
export const changeStudentAssignment = createAsyncThunk<
  void,
  {
    id: string;
    textId: string;
    invitationId?: string;
    filters?: AssignmentFilters;
  },
  { rejectValue: string; state: { materials: MaterialsState } }
>(
  "studentAssignments/change",
  async (
    { id, textId, invitationId, filters },
    { dispatch, getState, rejectWithValue },
  ): Promise<void | ReturnType<typeof rejectWithValue>> => {
    try {
      if (invitationId)
        await sharedApi.materials.revoke(textId, invitationId, getAuthToken());
      else await sharedApi.materials.assign(textId, id, getAuthToken());
      if (getState().materials.studentAssignments.id === id)
        await dispatch(fetchStudentAssignments({ id, filters })).unwrap();
    } catch (error: unknown) {
      return rejectWithValue(errorCode(error));
    }
  },
);
export const fetchReceivedMaterials = createAsyncThunk<
  { materials: ReceivedMaterial[]; total: number },
  void,
  { rejectValue: string; state: { materials: MaterialsState } }
>(
  "materials/received",
  async (
    _,
    { getState, rejectWithValue },
  ): Promise<
    | { materials: ReceivedMaterial[]; total: number }
    | ReturnType<typeof rejectWithValue>
  > => {
    try {
      return await sharedApi.materials.received(
        getState().materials.filters,
        getAuthToken(),
      );
    } catch (error: unknown) {
      return rejectWithValue(errorCode(error));
    }
  },
);
export const fetchMaterialInvitations = createAsyncThunk<
  MaterialInvitation[],
  string,
  { rejectValue: string }
>(
  "materials/invitations",
  async (
    id,
    { rejectWithValue },
  ): Promise<MaterialInvitation[] | ReturnType<typeof rejectWithValue>> => {
    try {
      return (await sharedApi.materials.invitations(id, getAuthToken()))
        .invitations;
    } catch (error: unknown) {
      return rejectWithValue(errorCode(error));
    }
  },
);
export const sendMaterialInvitation = createAsyncThunk<
  MaterialInvitation,
  { textId: string; relationshipId: string },
  { rejectValue: string }
>(
  "materials/send",
  async (
    { textId, relationshipId },
    { rejectWithValue },
  ): Promise<MaterialInvitation | ReturnType<typeof rejectWithValue>> => {
    try {
      return await sharedApi.materials.assign(
        textId,
        relationshipId,
        getAuthToken(),
      );
    } catch (error: unknown) {
      return rejectWithValue(errorCode(error));
    }
  },
);
export const revokeMaterialInvitation = createAsyncThunk<
  string,
  { textId: string; id: string },
  { rejectValue: string }
>(
  "materials/revoke",
  async (
    { textId, id },
    { rejectWithValue },
  ): Promise<string | ReturnType<typeof rejectWithValue>> => {
    try {
      await sharedApi.materials.revoke(textId, id, getAuthToken());
      return id;
    } catch (error: unknown) {
      return rejectWithValue(errorCode(error));
    }
  },
);
export const updateReceivedMaterial = createAsyncThunk<
  void,
  { id: string; action: "decline" | "hide" | "restore" },
  { rejectValue: string; state: { materials: MaterialsState } }
>(
  "materials/update",
  async (
    { id, action },
    { dispatch, rejectWithValue },
  ): Promise<void | ReturnType<typeof rejectWithValue>> => {
    try {
      await sharedApi.materials.updateReceived(id, action, getAuthToken());
      await dispatch(fetchReceivedMaterials()).unwrap();
    } catch (error: unknown) {
      return rejectWithValue(errorCode(error));
    }
  },
);
const slice = createSlice({
  name: "materials",
  initialState,
  reducers: {
    closeStudentAssignments: (state): void => {
      state.studentAssignments = initialState.studentAssignments;
    },
    resetMaterials: (): MaterialsState => initialState,
    setMaterialFilters: (
      state,
      action: PayloadAction<Partial<MaterialsState["filters"]>>,
    ): void => {
      state.filters = { ...state.filters, ...action.payload };
    },
  },
  extraReducers: (builder): void => {
    builder.addCase(toggleFavorite.fulfilled, (state, action): void => {
      const material = state.received.find(
        (item): boolean => item.text.id === action.payload.textId,
      );
      if (material) material.text.isFavorited = action.payload.favorited;
    });
    builder.addCase(fetchStudentAssignments.pending, (state, action): void => {
      const data = state.studentAssignments;
      if (data.id !== action.meta.arg.id) {
        data.assignments = [];
        data.catalog = [];
        data.busy = false;
      }
      data.id = action.meta.arg.id;
      data.requestId = action.meta.requestId;
      data.loading = true;
      data.error = null;
    });
    builder.addCase(
      fetchStudentAssignments.fulfilled,
      (state, action): void => {
        if (state.studentAssignments.requestId !== action.meta.requestId)
          return;
        Object.assign(state.studentAssignments, action.payload, {
          loading: false,
        });
      },
    );
    builder.addCase(fetchStudentAssignments.rejected, (state, action): void => {
      if (state.studentAssignments.requestId !== action.meta.requestId) return;
      state.studentAssignments.loading = false;
      state.studentAssignments.error =
        action.payload || "MATERIAL_TECHNICAL_ERROR";
    });
    builder.addCase(changeStudentAssignment.pending, (state): void => {
      state.studentAssignments.busy = true;
      state.studentAssignments.error = null;
    });
    builder.addCase(
      changeStudentAssignment.fulfilled,
      (state, action): void => {
        if (state.studentAssignments.id === action.meta.arg.id)
          state.studentAssignments.busy = false;
      },
    );
    builder.addCase(changeStudentAssignment.rejected, (state, action): void => {
      if (state.studentAssignments.id !== action.meta.arg.id) return;
      state.studentAssignments.busy = false;
      state.studentAssignments.error =
        action.payload || "MATERIAL_TECHNICAL_ERROR";
    });
    builder.addCase(fetchReceivedMaterials.pending, (state, action): void => {
      state.receivedRequestId = action.meta.requestId;
      state.loading = true;
      state.error = null;
    });
    builder.addCase(fetchReceivedMaterials.fulfilled, (state, action): void => {
      if (state.receivedRequestId !== action.meta.requestId) return;
      state.loading = false;
      state.received = action.payload.materials;
      state.total = action.payload.total;
    });
    builder.addCase(fetchMaterialInvitations.pending, (state, action): void => {
      state.invitationsTextId = action.meta.arg;
      state.invitations = [];
      state.error = null;
    });
    builder.addCase(
      fetchMaterialInvitations.fulfilled,
      (state, action): void => {
        if (state.invitationsTextId !== action.meta.arg) return;
        state.invitations = action.payload;
      },
    );
    builder.addCase(sendMaterialInvitation.fulfilled, (state, action): void => {
      if (state.invitationsTextId !== action.payload.textId) return;
      const index = state.invitations.findIndex(
        (item): boolean => item.id === action.payload.id,
      );
      if (index >= 0) state.invitations[index] = action.payload;
      else state.invitations.unshift(action.payload);
    });
    builder.addCase(
      revokeMaterialInvitation.fulfilled,
      (state, action): void => {
        const entry = state.invitations.find(
          (item): boolean => item.id === action.payload,
        );
        if (entry) entry.status = "REVOKED";
      },
    );
    builder.addMatcher(
      (action): boolean =>
        action.type.startsWith("materials/") &&
        action.type.endsWith("/pending"),
      (state): void => {
        state.busy = true;
        state.error = null;
      },
    );
    builder.addMatcher(
      (action): boolean =>
        action.type.startsWith("materials/") &&
        action.type.endsWith("/fulfilled"),
      (state): void => {
        state.busy = false;
      },
    );
    builder.addMatcher(
      (action): action is PayloadAction<string> =>
        action.type.startsWith("materials/") &&
        action.type.endsWith("/rejected"),
      (state, action): void => {
        state.busy = false;
        state.loading = false;
        state.error = action.payload || "MATERIAL_TECHNICAL_ERROR";
      },
    );
  },
});
export const { resetMaterials, setMaterialFilters, closeStudentAssignments } =
  slice.actions;
export default slice.reducer;
